/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {useEffect, useState} from 'react'
import '../../../camera/CameraPerformance.css'
import {BottomButtonsWrapper} from '../../../camera/SensorCamera/ItemDetails/DetectionCapability/styled';
import {useMsal} from '@azure/msal-react';
import ArgosButton from "../../../../components/Buttons/ArgosButton";
import {useAppSelector} from "../../../../../redux/hooks";
import ArgosTextField from "../../../../components/Form/TextField/ArgosTextField";
import ArgosFlexbox from "../../../../components/Containers/ArgosFlexbox";
import {selectGlobalDialogState} from '../../../../../redux/ducks/GlobalDialog/slice';
import {useHasWriteAccess} from "../../../../../redux/ducks/AccessControl/accessControlHelpers";

import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
} from '@mui/material';
import {usePostRequest} from "../../../../../api/helper";
import RightPanel from './CollapsibleTabs/RightPanel';
import ObjectProperties from './CollapsibleTabs/ObjectProperties';
import UltrasonicProperties from './CollapsibleTabs/UltrasonicProperties';
import ArgosCollapseComponent from '../../../../components/CollapseComponent/ArgosCollapseComponent';
import {theme} from '../../../../../constants/theme';
import {getUltrasonicPlotEndpoint, } from '../../constants';
import { toast } from 'react-hot-toast';
import VAR_COMMON_CONSTANTS from "../../../../../constants/globalVariables";
import { COLUMN_NAMES } from '../constants';


const Content = (props: any) => {

  const buildPlotData = (_data: any) => {
    const data: any = {x: _data?.x, y: _data?.y}
    const columns = {xLabel: COLUMN_NAMES.AZIMUTH_ANGLE, yLabel: COLUMN_NAMES.DISTANCE}
    const returnObj = {data, columns}
    return returnObj
  }

  const hasWriteAccess = useHasWriteAccess()
  const [baselineData, setBaselineData] = useState(props.baselineData)
  const [plotData, setPlotData] = useState(buildPlotData(props.sensorUltrasonicData.plotData[Object.keys(props.sensorUltrasonicData.plotData)[0]]?.data))
  const [fovAndBoresightUltrasonicValues, setFovAndBoresightUltrasonicValues] = useState<any>({});
  const [objectData, setObjectData] = useState(props.objectData);
  const [objectDataMap, setObjectDataMap] = useState(props.objectDataMap);
  const [configName, setConfigName] = useState("");
  const [openSavePopup, setOpenSavePopup] = useState(false);
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [isLoadingPlot, setIsLoadingPlot] = useState(false);
  const gmid = props.gmid
  const isDialogOpen = useAppSelector(selectGlobalDialogState).isOpen
  const [isError, setIsError] = useState(false);
  const [componentDataMap, setComponentDataMap] = useState<any>({});
  const [temperatureArray, setTemperatureArray] = useState<number[]>([]);
  const [sensorUltrasonicData, setSensorUltrasonicData] = useState<any>(props?.sensorUltrasonicData)
  useEffect(() => {
    setConfigName(baselineData['configName'])
  }, [baselineData?.['configName']]);

  useEffect(() => {
    setBaselineData(props.baselineData);
    setObjectData(props.objectData);
    setObjectDataMap(props.objectDataMap);
    let _plotData = props.sensorUltrasonicData?.plotData[getPlotDataName(props.baselineData.temperature)]?.data
    setPlotData(buildPlotData(_plotData))
    getTemperatureArray();
  }, [props])

  useEffect(() => {
    handleUpdatePlot(props.baselineData)
  }, [props.configIndex])


  const [newObjectName, setNewObjectName] = useState<string>( props?.tempToObjMap[23]["objectName"]);
  function handleTempChange(newTempValue: string ) {
    const _newObjectName: any =  props?.tempToObjMap[newTempValue as unknown as number]["objectName"]
    const _firingMode: any =  props?.tempToObjMap[newTempValue as unknown as number]["firingMode"]
    const newObject = {
      ...baselineData,
      "firingMode": _firingMode,
      "objectName": _newObjectName,
      'temperature': newTempValue
    }
    setSensorUltrasonicData({
      ...sensorUltrasonicData,
      "firingMode": _firingMode
    })
    setNewObjectName(_newObjectName)
    setBaselineData(newObject)
  }

  const getTemperatureArray = () => {
    const sensorData = props.sensorUltrasonicData.plotData
    let _temperatureArray: number[] = [];
    Object.keys(sensorData)?.forEach((key:string) => {
      if(key != "instrumentationLimits"){
        const detectiondata = sensorData[key].data
        const tempValue = Object.keys(detectiondata).filter((keys: string) => keys.includes("temperature") &&  keys.includes("value"))[0] as unknown as number
        _temperatureArray.push(detectiondata[tempValue])
      }
    })
    setTemperatureArray(_temperatureArray)
  }
  const getObjectIdBasedName = () => {
    const objectId = objectData.filter((objectElement: any) => objectElement.name === baselineData.objectName)[0]?.id
    return objectId
  }
  const url = getUltrasonicPlotEndpoint
  const getUltrasonicPlotHandle = usePostRequest(url)
  async function handleUpdatePlot(baselineData: any) {
    setIsLoadingPlot(true)
    let newPlotData = {
      'objectId': getObjectIdBasedName(),
      'instrumentationRange' : baselineData["maxInstrumentedRange"],
      'instrumentationAngle':  baselineData["azimuthFOV"],
      "plotData": props.sensorUltrasonicData.plotData[getPlotDataName(baselineData.temperature)].data,
    }
    getUltrasonicPlotHandle(newPlotData).then((responseJson) => {
      setIsError(false)
      const fovAndBoresightUltrasonicObject = {
        'azimuth_fov': responseJson["azimuth_fov"],
        'elevation_fov': responseJson["elevation_fov"],
        'max_range': responseJson["max_range"]
      }
      const data = {
        'x': responseJson['x'],
        'y': responseJson['y']
      }
      setPlotData(buildPlotData(data))
      setFovAndBoresightUltrasonicValues(fovAndBoresightUltrasonicObject)
      setIsLoadingPlot(false);
    }).catch(err => {
      toast.error("Error while updating plot. Please check your values and try again")
      setIsError(true)
      setIsLoadingPlot(false)
    })
  }

  function handleReset() {
    setBaselineData(props.baselineData)
  }

  function getPlotDataName(_tempValue: string) {
    const sensorData = props.sensorUltrasonicData.plotData
    let detectionDataKey: any = ""
    Object.keys(sensorData)?.forEach((key:string) => {
      if(key != "instrumentationLimits"){
        const detectiondata = sensorData[key].data
        const tempValue = Object.keys(detectiondata).filter((keys: string) => keys.includes("temperature") &&  keys.includes("value"))[0]
        if(detectiondata[tempValue] == _tempValue){
          detectionDataKey = key
        }
      }
      
    })
    return detectionDataKey
  }

  function onSaveConfiguration() {
    props.onSaveConfiguration(null, baselineData, configName, gmid)
  }

  function onOverrideConfiguration() {
    props.onSaveConfiguration(props.configIndex - 1, baselineData, configName, gmid)
  }

  const handleClose = () => {
    setOpenSavePopup(false);
    setOpenDeletePopup(false);
  };

  const handleClickSaveOpen = () => {
    setOpenSavePopup(true);
  };

  function handleSaveConfigName(e: any) {
    setConfigName(e.target.value)
  }

  function onDeleteConfiguration() {
    props.onDeleteConfiguration(props.baselineData.configName, props.baselineData.owner)
  }

  function setIsPublicConfig(_isPublicConfig: any) {
    const newObject = {
      ...baselineData,
      'isPublicConfig': _isPublicConfig
    }
    setBaselineData(newObject);
  }

  useEffect(() => {
    createCollapsibleTabs()
  }, [props, baselineData, objectDataMap, plotData, openSavePopup, configName])

  const createCollapsibleTabs = () => {
    let _componentDataMap: any = {}
    _componentDataMap["Ultrasonic Properties"] = <UltrasonicProperties
      temperatureArray={temperatureArray}
      sensorUltrasonicData={sensorUltrasonicData}
      isDialogOpen={isDialogOpen}
      handleTempChange={handleTempChange}
      baselineData={baselineData}
    />
    _componentDataMap[VAR_COMMON_CONSTANTS.sensorApp.all.SENSOR_TESTING_CONDITIONS_HEADER] = <ObjectProperties
      temperatureArray={temperatureArray}
      handleTempChange={handleTempChange}
      baselineData={baselineData}
      isDialogOpen={isDialogOpen}
      gmid={gmid}
      configIndex={gmid}
      newObjectName={newObjectName}
      setIsPublicConfig={setIsPublicConfig}
    />
    _componentDataMap["Sensor Performance"] = <RightPanel 
      isLoadingPlot={isLoadingPlot}
      isError={isError}
      plotData={plotData}
      handleUpdatePlot={handleUpdatePlot}
      baselineData={baselineData}
      handleReset={handleReset}
      handleClickSaveOpen={handleClickSaveOpen}
      openSavePopup={openSavePopup}
      handleClose={handleClose}
      handleSaveConfigName={handleSaveConfigName}
      configName={configName}
      configIndex={props?.configIndex}
      gmid={gmid}
      onOverrideConfiguration={onOverrideConfiguration}
      onSaveConfiguration={onSaveConfiguration}
      onDeleteConfiguration={onDeleteConfiguration}
      fovAndBoresightUltrasonicValues={fovAndBoresightUltrasonicValues}
    />
    setComponentDataMap(_componentDataMap)
  }

  return (
    <ArgosFlexbox vertical style={{overflow: 'auto', height: '100%', width: '100%'}}>
      {/* Make it own component */}
      <ArgosFlexbox style={{alignContent: 'center'}}>
        <BottomButtonsWrapper style={{marginTop: '0px'}}>
          <ArgosButton disabled={isLoadingPlot} onClick={() => handleUpdatePlot(baselineData)}>Update Plot</ArgosButton>
        </BottomButtonsWrapper>
      </ArgosFlexbox>
     <div style={{ flex: 1, overflow: 'auto' }}>
      <ArgosCollapseComponent
        styleProps={{
          outlineOnly: true,
          fontColor: theme.layout.sidebar.backgroundColor,
        }}
        defaultOpen={true}
        data={componentDataMap ?? {}}
      />
    </div>
        
    </ArgosFlexbox>
  )
}
export default Content